import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { CONTACT } from '../../config';
import { getVisitorId } from '../../utils/visitorTracking';
import { getTurnstileToken } from '../../utils/turnstileToken';

const CONTACT_ENDPOINT = 'https://cynzv51q56.execute-api.eu-south-2.amazonaws.com/dev/contact';

const TEXTS = {
  es: {
    name: 'Nombre',
    email: 'Correo electrónico',
    company: 'Empresa',
    phone: 'Teléfono',
    message: 'Mensaje',
    optional: '(opcional)',
    namePlaceholder: 'Su nombre',
    emailPlaceholder: 'nombre@empresa.com',
    companyPlaceholder: 'Nombre de su empresa',
    phonePlaceholder: '+34 600 000 000',
    privacy: 'Acepto la',
    privacyLink: 'política de privacidad',
    submitting: 'Enviando...',
    errorSubmit: 'No se ha podido enviar el formulario. Inténtelo de nuevo.',
    errorTooFast: 'Tómese un momento para revisar el formulario.',
    message_: {
      messagePlaceholder: '¿En qué podemos ayudarle?',
      submit: 'Enviar mensaje',
      subject: 'Formulario de contacto',
      defaultMessage: 'Mensaje desde el formulario de contacto',
      successTitle: 'Mensaje enviado',
      successDesc: 'Hemos recibido su consulta. Le respondemos lo antes posible.',
    },
    callback_: {
      messagePlaceholder: 'Indíquenos brevemente cuándo le viene bien...',
      submit: 'Solicitar llamada',
      subject: 'Quiere que le llamemos',
      defaultMessage: 'Pide que le llamemos.',
      successTitle: 'Solicitud recibida',
      successDesc: `Le llamamos en horario de atención (${CONTACT.phoneHours.es}).`,
    },
  },
  en: {
    name: 'Name',
    email: 'Email',
    company: 'Company',
    phone: 'Phone',
    message: 'Message',
    optional: '(optional)',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'you@company.com',
    companyPlaceholder: 'Company name',
    phonePlaceholder: '+34 600 000 000',
    privacy: 'I accept the',
    privacyLink: 'privacy policy',
    submitting: 'Sending...',
    errorSubmit: 'Error sending the form. Please try again.',
    errorTooFast: 'Please take a moment to review the form.',
    message_: {
      messagePlaceholder: 'Tell us how we can help...',
      submit: 'Send message',
      subject: 'Contact form',
      defaultMessage: 'Message from the contact form',
      successTitle: 'Message sent',
      successDesc: 'We have received your enquiry. We will get back to you as soon as possible.',
    },
    callback_: {
      messagePlaceholder: 'Briefly tell us when suits you...',
      submit: 'Request call',
      subject: 'Requests a call',
      defaultMessage: 'Asks us to call.',
      successTitle: 'Request received',
      successDesc: `We will call you during business hours (${CONTACT.phoneHours.en}).`,
    },
  },
};

const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

/**
 * Formulario de contacto de la web pública. Dos variantes:
 *  - `message`: consulta por escrito (mensaje obligatorio, teléfono opcional).
 *  - `callback`: «quiero que me llamen» (teléfono obligatorio, mensaje opcional).
 * Ambas envían a la lambda contactForm; `kind` y `origin` llegan en `source`
 * y `utmSource` para distinguirlas en el correo y en el CRM.
 */
const ContactRequestForm = ({ kind = 'message', lang = 'es', origin = '' }) => {
  const t = TEXTS[lang] || TEXTS.es;
  const k = kind === 'callback' ? t.callback_ : t.message_;
  const isCallback = kind === 'callback';

  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '', message: '', privacy: false, honeypot: ''
  });
  const [timestamp] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const trackSalesIQ = (eventData) => {
    if (window.$zoho && window.$zoho.salesiq) {
      window.$zoho.salesiq.visitor.customaction(JSON.stringify(eventData));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;

    const timeSinceLoad = Date.now() - timestamp;
    if (timeSinceLoad < 3000) {
      setNotification({ type: 'warning', message: t.errorTooFast });
      return;
    }

    setIsSubmitting(true);
    trackSalesIQ({
      action: isCallback ? 'callback_request_submitted' : 'contact_form_submitted',
      name: formData.name,
      email: formData.email,
      company: formData.company,
    });

    try {
      const companyPart = formData.company ? ` / ${formData.company}` : '';
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subject: `${k.subject}${companyPart}`,
          message: formData.message || k.defaultMessage,
          timestamp,
          timeSinceLoad,
          turnstileToken: await getTurnstileToken(),
          source: isCallback ? 'callback-request' : 'contact-form',
          utmSource: origin,
          visitorId: getVisitorId(),
        }),
      });

      if (!response.ok) throw new Error('Error al enviar');

      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11012055024/4B25CKWA9qIaEPC_-oIp',
          'value': 1.0,
          'currency': 'EUR',
        });
      }
      setSubmitted(true);
      setNotification(null);
    } catch (error) {
      trackSalesIQ({ action: isCallback ? 'callback_request_error' : 'contact_form_error', error: error.message });
      setNotification({ type: 'error', message: t.errorSubmit });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="animate-fade-in text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">{k.successTitle}</h2>
        <p className="text-gray-600">{k.successDesc}</p>
      </div>
    );
  }

  const idPrefix = isCallback ? 'cb' : 'cf';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input
        type="text"
        name="honeypot"
        value={formData.honeypot}
        onChange={handleChange}
        style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
        tabIndex="-1"
        autoComplete="off"
      />

      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium text-gray-700 mb-1">
          {t.name} *
        </label>
        <input
          type="text" id={`${idPrefix}-name`} name="name" required
          value={formData.name} onChange={handleChange}
          className={inputClass} placeholder={t.namePlaceholder}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-email`} className="block text-sm font-medium text-gray-700 mb-1">
          {t.email} *
        </label>
        <input
          type="email" id={`${idPrefix}-email`} name="email" required
          value={formData.email} onChange={handleChange}
          className={inputClass} placeholder={t.emailPlaceholder}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-company`} className="block text-sm font-medium text-gray-700 mb-1">
          {t.company}
        </label>
        <input
          type="text" id={`${idPrefix}-company`} name="company"
          value={formData.company} onChange={handleChange}
          className={inputClass} placeholder={t.companyPlaceholder}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-phone`} className="block text-sm font-medium text-gray-700 mb-1">
          {t.phone} {isCallback ? '*' : t.optional}
        </label>
        <input
          type="tel" id={`${idPrefix}-phone`} name="phone" required={isCallback}
          value={formData.phone} onChange={handleChange}
          className={inputClass} placeholder={t.phonePlaceholder}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-message`} className="block text-sm font-medium text-gray-700 mb-1">
          {t.message} {isCallback ? t.optional : '*'}
        </label>
        <textarea
          id={`${idPrefix}-message`} name="message" rows={isCallback ? 2 : 4} required={!isCallback}
          value={formData.message} onChange={handleChange}
          className={inputClass} placeholder={k.messagePlaceholder}
        />
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox" id={`${idPrefix}-privacy`} name="privacy" required
          checked={formData.privacy} onChange={handleChange}
          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor={`${idPrefix}-privacy`} className="text-sm text-gray-600">
          {t.privacy}{' '}
          <a href={lang === 'en' ? '/en/privacy' : '/privacidad'} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
            {t.privacyLink}
          </a>
        </label>
      </div>

      {notification && (
        <div className={`p-3 rounded-lg text-sm ${
          notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-yellow-50 text-yellow-800 border border-yellow-200'
        }`}>
          {notification.message}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-700 text-white font-semibold hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Send className="w-4 h-4" />
        {isSubmitting ? t.submitting : k.submit}
      </button>
    </form>
  );
};

export default ContactRequestForm;
